import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {
  IGamesFilterService,
  IPcFilterIndicatorRenderingOptions
} from './games-filter.service.interface';
import {ServiceBase} from '../ServiceBase';
import {IGamesFilterStrategy} from "./strategies/games-filter-strategy.interface";
import {GameInfo} from "../games/game-info";
import React from "react";
import {action, computed, makeObservable, observable} from "mobx";
import {Nullable} from "../../../common/types/nullable-types";
import {NoGamesFilterStrategy} from "./strategies/no-filter/no-games-filter.strategy";
import {IArenaViewModel} from '../arenas/models/arenas/arena-view-model.interface';

export class GamesFilterService extends ServiceBase implements IGamesFilterService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);

    this._currentFilteringStrategy = new NoGamesFilterStrategy(this.services);

    this.reaction(() => this.services.routing.currentRoute, () => {
      this._setCurrentStrategy(this.services.arenas.getCurrentArena())
    }, {
      fireImmediately: true
    });
  }

  @action.bound
  private _setCurrentStrategy(currentArena: IArenaViewModel) {
    if (this._currentFilteringStrategy) {
      this._currentFilteringStrategy.dispose();
    }

    if(currentArena) {
      this._currentFilteringStrategy = currentArena.getGamesFilteringStrategy();
    } else {
      this._currentFilteringStrategy = new NoGamesFilterStrategy(this.services);
    }
  }


  get filteredGamesMap(): Record<string,GameInfo> {
    return this._currentFilteringStrategy.filteredGamesMap;
  }


  @computed
  get currentAppliedFiltersIds(): string[] {
    const urlHash = this.services.location.currentUri.hash?.value;
    if(urlHash) {
      let params = urlHash.split('=');
      if(params[0]?.toLowerCase() === 'filters') {
        return params[1].split(',');
      }
    }

    return [];

  }

  async applyFilters(filterIds: string[]): Promise<void> {
    this.services.window.scrollTo(0, 0);

    if(filterIds.length > 0) {
      await this.services.location.push('#filters=' + filterIds.join(','));
    } else {
      await this.services.location.push(this.services.location.currentUri.pathName.toString());
    }
  }

  async clearFilters(): Promise<void> {
    await this.services.location.push(this.services.location.currentUri.pathName.toString());
  }

  @observable.ref
  private _currentFilteringStrategy: IGamesFilterStrategy;

  get isFilterOn(): boolean {
    return this.currentAppliedFiltersIds.length > 0;
  }

  get isFilteringAvailable(): boolean {
    return this._currentFilteringStrategy.isFilteringAvailable;
  }

  isFilterApplied(filterId: string): boolean {
    return this.currentAppliedFiltersIds.includes(filterId);
  }

  renderMobileFiltersIndicator(): React.ReactElement {
    return this._currentFilteringStrategy.renderMobileFiltersIndicator();
  }

  renderPCFiltersIndicator(): Nullable<IPcFilterIndicatorRenderingOptions> {
    return this._currentFilteringStrategy.renderPCFiltersIndicator();
  }

  showFiltersEditor(): void {
    this._currentFilteringStrategy.showFiltersEditor();
  }
}
